import React from 'react'
import { Download, RefreshCw } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { track } from '../../../../lib/analytics/posthog'
import type { LoadingActivity } from '../../state/editor-store'
import Button from '../../../../components/ui/button'
import AnimatedIconButton from '../../../../components/ui/animated-icon-button'

// Inline animated ellipsis component (three dots that rise/fade sequentially)
const AnimatedEllipsis: React.FC = () => {
  return (
    <span className="inline-flex ml-1 align-baseline" aria-hidden="true">
      {[0, 1, 2].map(i => (
        <span
          key={i}
          className="w-1 h-1 mx-[2px] rounded-full bg-brand-600/70 animate-script-dot"
          style={{ animationDelay: `${i * 0.25}s` }}
        />
      ))}
    </span>
  )
}

// Unified waiting + generating state component
// Shows an initial waiting message, then updates with first activity message.
// Always displays the guidance about leaving the page.
export const WaitingForScriptState: React.FC<{ activity?: LoadingActivity | null }> = ({ activity }) => {
  const navigate = useNavigate()
  const hasActivity = !!activity?.message
  const dynamicStatus = hasActivity ? activity.message : 'Generating your script…'
  const percent = typeof activity?.percent === 'number' ? Math.round(activity.percent) : null

  return (
    <div className="relative p-8 flex items-center justify-center flex-1" data-tourid="full-editor-loader">
      {/* Center status card */}
      <div className="text-center max-w-md space-y-4">
        {/* Promo CTA: Turn it into a Series (moved above activity message) */}
        <div className="relative overflow-hidden rounded-xl border border-black/5 bg-white/70 backdrop-blur-sm shadow-[0_1px_0_rgba(0,0,0,0.04)]">
          <div className="absolute -inset-x-6 -top-8 h-24 bg-gradient-to-br from-brand-600/10 via-transparent to-transparent pointer-events-none" aria-hidden="true" />
          <div className="px-4 py-3 sm:px-5 sm:py-4">
            <div className="text-center">
              <div className="flex items-center justify-center gap-2 mb-1">
                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-brand-50 text-brand-700 border border-brand-600/20">New</span>
                <span className="text-sm font-medium text-foreground">Turn this into a Series</span>
              </div>
              <p className="text-[12px] text-foreground/60">You just generated a single podcast, manually. Use our new Series feature to get new episodes on your schedule, automatically.</p>
            </div>
            <div className="mt-3 flex justify-center">
              <Button
                size="sm"
                className="gap-2 shadow-sm hover:shadow-md transition-shadow duration-200"
                onClick={() => {
                  track('series_promo_click', { placement: 'editor_loader' })
                  navigate('/series/new')
                }}
              >
                Create series
              </Button>
            </div>
          </div>
        </div>

        {/* Removed spinner icon; focusing on cleaner minimalist status line */}
        <div className="space-y-2">
          <h3 className="text-lg font-medium text-foreground" aria-live="polite">
            <span>{dynamicStatus}</span>
            <AnimatedEllipsis />
          </h3>
          <p className="text-xs text-foreground/50">
            This can take a {activity?.message?.includes('Updating script:') ? 'minute' : 'few minutes'}. You can leave this page and come back later, your podcast will be waiting for you.
          </p>
          {percent !== null && (
            <div className="mt-4 w-full max-w-xs mx-auto">
              <div className="h-1.5 w-full rounded-full bg-surface-200 overflow-hidden">
                <div
                  className="h-full bg-brand-600 transition-all duration-300"
                  style={{ width: `${percent}%` }}
                  aria-valuenow={percent}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  role="progressbar"
                />
              </div>
              <span className="mt-1.5 text-[10px] uppercase tracking-wide font-medium text-foreground/50 float-right">{percent}%</span>
            </div>
          )}
        </div>
        <div className="w-full flex justify-center items-center space-x-2 mt-6">
          <span className="text-[10px] text-foreground/50 italic">
            If the status doesn't update, try refreshing
          </span>
          <AnimatedIconButton
            icon={<RefreshCw className="w-3.5 h-3.5" />}
            variant="ghost"
            size="xs"
            aria-label="Refresh status"
            className="text-foreground/40 hover:text-foreground/70"
            onClick={() => window.location.reload()}
          />
        </div>

        
      </div>
      {/* Mock generate-all button (for onboarding tour) */}
      {activity?.phase !== 'script-refine' && <div className="pointer-events-none absolute bottom-4 right-4 md:bottom-6 md:right-8" data-tourid="mock-generate-all-button">
        <Button
          type="button"
          disabled
          className="gap-2 cursor-not-allowed opacity-60"
          aria-disabled="true"
          aria-label="Generate all audio (unavailable while script is generating)"
        >
          <Download className="h-4 w-4" />
          <span>Generate all audio</span>
        </Button>
      </div>}
      {/* Local keyframes (scoped) */}
      <style>
        {`
          @keyframes script-dot-seq { 
            0%, 60%, 100% { transform: translateY(0); opacity: .25; }
            30% { transform: translateY(-2px); opacity: 1; }
          }
          .animate-script-dot { 
            animation: script-dot-seq 1.5s ease-in-out infinite; /* slowed from 1s */
          }
        `}
      </style>
    </div>
  )
}
